import { Component, computed, inject, OnInit, signal, viewChild, ChangeDetectionStrategy } from '@angular/core';
import { AssemblyArtifact, XmlArtifact } from '../models/artifact';
import { GenericModalService, ModalSettingsTemplate } from '../../shared/generic-modal/generic-modal.service';
import { DeveloperArtifactService } from '../services/developer-artifact.service';
import { LoadingPanelService } from '../../shared/loading-panel/loading-panel.service';
import { NugetFeedPackage } from '../models/nuget-feed-package';
import { NugetFeedConfigurationDialogComponent } from './nuget-feed-configuration-dialog/nuget-feed-configuration-dialog.component';
import { NugetFeed } from '../models/nuget-feed';
import { NugetPackageContent } from '../models/nuget-package-content';
import { NugetPackage } from '../models/nuget-package';
import { NugetPackageInstallDialogComponent } from './nuget-package-install-dialog/nuget-package-install-dialog.component';
import { DxTreeViewComponent, DxTreeViewModule, DxLoadIndicatorModule } from 'devextreme-angular';
import { ItemClickEvent, Node } from 'devextreme/ui/tree_view';
import { SvgIconComponent } from '../../shared/icons/svg-icon.component';
import { FormsModule } from '@angular/forms';
import { DxTemplateModule } from 'devextreme-angular/core';
import { CollapsibleGroupComponent } from '../../shared/collapsible-group/collapsible-group.component';
import { MarkdownModule } from 'ngx-markdown';

export interface NugetPackageSelection {
    NugetFeedPackage: NugetFeedPackage;
    NugetFeed: NugetFeed;
}

@Component({
    selector: 'app-developer-nuget-feeds',
    templateUrl: './developer-nuget-feeds.component.html',
    styleUrls: ['./developer-nuget-feeds.component.scss'],
    imports: [SvgIconComponent, FormsModule, DxTreeViewModule, DxTemplateModule, CollapsibleGroupComponent, DxLoadIndicatorModule, MarkdownModule],
    changeDetection: ChangeDetectionStrategy.Eager,
    preserveWhitespaces: true // For markdown formatting
})
export class DeveloperNugetFeedsComponent implements OnInit {
    private readonly developerArtifactService = inject(DeveloperArtifactService);
    private readonly modalService = inject(GenericModalService);
    private readonly loadingPanelService = inject(LoadingPanelService);
    private readonly treeView = viewChild.required<DxTreeViewComponent>('feedTreeView');

    nugetFeeds: NugetFeed[] = [];
    currentPackageXmlContent: XmlArtifact[] = [];
    currentPackageAssemblyContent: AssemblyArtifact[] = [];
    isLoadingPackageContent = false;
    packageContentHidden = false;
    includePrerelease = false;
    searchTerm = '';

    currentSelection = signal<NugetPackageSelection>(null);
    currentDescription = computed(() => {
        const selection = this.currentSelection();
        if (selection?.NugetFeedPackage) {
            return selection.NugetFeedPackage.Description;
        }
        return '';
    });

    ngOnInit(): void {
        void this.refresh(true);
    }

    async refresh(clearSearch: boolean): Promise<void> {
        this.nugetFeeds = [];
        this.currentSelection.set(null);
        this.searchTerm = clearSearch ? '' : this.searchTerm;

        this.loadingPanelService.showLoadingPanelDirect('Loading nuget feeds...');
        try {
            const feeds: NugetFeed[] = await this.developerArtifactService.getNugetFeeds();
            if (feeds.length > 0) {
                const populatedFeeds: NugetFeed[] = await Promise.all(feeds.map(async feed => {
                    try {
                        const packages = await this.developerArtifactService.getNugetFeedInfo(feed.Id, this.includePrerelease, this.searchTerm);
                        return { ...feed, Packages: packages };
                    } catch (error: unknown) {
                        return { ...feed, LoadError: (error as { error: string; })?.error };
                    }
                }));

                this.nugetFeeds = populatedFeeds.sort((a, b) => b.Name.localeCompare(a.Name));

                if (this.searchTerm !== '') {
                    // Yield to the next microtask tick so Angular can render the updated nugetFeeds before calling expandAll().
                    await new Promise<void>(resolve => setTimeout(resolve));
                    this.treeView().instance.expandAll();
                }
            }
        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }
    }

    async openFeedConfiguration(): Promise<void> {
        const componentValues = { nugetFeeds: this.nugetFeeds };
        const result = await this.modalService.openComponentType<boolean>(NugetFeedConfigurationDialogComponent, componentValues, new ModalSettingsTemplate('lg', 'static'));
        if (result) {
            await this.refresh(true);
        }
    }

    async onContentPanelStateChange(event: boolean): Promise<void> {
        if (event === false) {
            const nugetPackageId: string = this.currentSelection().NugetFeedPackage.Identity.Id;
            const nugetPackageVersion: string = this.currentSelection().NugetFeedPackage.Identity.Version.OriginalVersion;
            const nugetFeedId: number = this.currentSelection().NugetFeed.Id;
            await this.loadPackageContent(nugetPackageId, nugetPackageVersion, nugetFeedId);
        }
    }

    async loadPackageContent(nugetPackageId: string, nugetPackageVersion: string, nugetFeedId: number): Promise<void> {
        this.isLoadingPackageContent = true;
        try {
            const packages: NugetPackage[] = [{
                NugetPackageId: nugetPackageId,
                NugetPackageVersion: nugetPackageVersion,
                NugetFeedId: nugetFeedId
            }];

            const packageContents: NugetPackageContent[] = await this.developerArtifactService.getNugetPackages(packages);
            if (packageContents.length > 0) {
                const packageContent: NugetPackageContent = packageContents[0];
                this.currentPackageXmlContent = packageContent.XmlArtifacts;
                this.currentPackageAssemblyContent = packageContent.AssemblyArtifacts;
            }
        } finally {
            this.isLoadingPackageContent = false;
        }
    }

    async installCurrentPackage(): Promise<void> {
        await this.installPackages(this.currentSelection());
    }

    async installPackages(packageSelections: NugetPackageSelection | NugetPackageSelection[]): Promise<void> {
        const normalizedPackageSelections: NugetPackageSelection[] = Array.isArray(packageSelections) ? packageSelections : [packageSelections];
        const packages: NugetPackage[] = normalizedPackageSelections.map(p => ({
            NugetPackageId: p.NugetFeedPackage.Identity.Id,
            NugetPackageVersion: p.NugetFeedPackage.Identity.Version.OriginalVersion,
            NugetFeedId: p.NugetFeed.Id
        }));

        let packageContents: NugetPackageContent[];
        this.loadingPanelService.showLoadingPanelDirect('Loading package content...');
        try {
            packageContents = await this.developerArtifactService.getNugetPackages(packages);
        } finally {
            this.loadingPanelService.hideLoadingPanel();
        }

        const nugetPackagesWithArtifacts: NugetPackageContent[] = packageContents.filter(p => p.AssemblyArtifacts.length > 0 || p.XmlArtifacts.length > 0);
        if (nugetPackagesWithArtifacts.length > 0) {
            const componentValues = { nugetPackages: packageContents, nugetFeeds: this.nugetFeeds };
            const settings = new ModalSettingsTemplate('lg', 'static');
            settings.windowClass = 'hugeModal';

            let confirmedNugetPackages: NugetPackageContent[];
            try {
                confirmedNugetPackages = await this.modalService.openComponentType<NugetPackageContent[]>(NugetPackageInstallDialogComponent, componentValues, settings);
            } catch {
                return; // User cancelled the dialog
            }

            const xmlArtifacts: XmlArtifact[] = confirmedNugetPackages.flatMap(p => p.XmlArtifacts);
            const assemblyArtifacts: AssemblyArtifact[] = confirmedNugetPackages.flatMap(p => p.AssemblyArtifacts);

            // Build a Map keyed by AssemblyName so duplicate names are automatically overwritten,
            // then spread the Map values back into an array to get one entry per unique AssemblyName.
            const uniqueAssemblyArtifacts: AssemblyArtifact[] = [...new Map(assemblyArtifacts.map(a => [a.AssemblyName, a])).values()];

            this.loadingPanelService.showLoadingPanelDirect('Saving artifacts...');
            try {
                await Promise.all([
                    this.developerArtifactService.saveXmlArtifacts(xmlArtifacts),
                    this.developerArtifactService.saveAssemblyArtifacts(uniqueAssemblyArtifacts)
                ]);
            } finally {
                this.loadingPanelService.hideLoadingPanel();
            }

            await this.modalService.messageDirect('Completed', 'Artifacts saved successfully.');
        } else {
            await this.modalService.messageDirect('No artifacts', 'This package contains no Link artifact.');
        }
    }

    private isNugetFeed(p: NugetFeed | NugetFeedPackage): p is NugetFeed {
        return 'Name' in p;
    }

    private isNugetPackage(p: NugetFeed | NugetFeedPackage): p is NugetFeedPackage {
        return 'Title' in p;
    }

    getText(item: NugetFeed | NugetFeedPackage): string | undefined {
        return this.isNugetFeed(item) ? item.Name : this.isNugetPackage(item) ? item.Title : undefined;
    }

    getIconUrl(item: NugetFeed | NugetFeedPackage): string | undefined {
        if (this.isNugetFeed(item)) {
            return item.LoadError ? '/images/warning.png' : '/images/nuget-feed.png';
        }
        if (this.isNugetPackage(item)) {
            return (item.IconUrl as string | undefined) ?? '/images/nuget-package.png';
        }
        return undefined;
    }

    treeViewItemClick(e: ItemClickEvent): void {
        this.currentSelection.set(this.getSelection(e.node as Node<NugetFeed | NugetFeedPackage>));
        this.currentPackageXmlContent = [];
        this.currentPackageAssemblyContent = [];
        this.packageContentHidden = true;
    }

    private getSelection(node: Node<NugetFeed | NugetFeedPackage>): NugetPackageSelection {
        const itemData = node.itemData as NugetFeed | NugetFeedPackage;
        return this.isNugetPackage(itemData)
            ? { NugetFeedPackage: itemData, NugetFeed: node.parent.itemData as NugetFeed }
            : { NugetFeedPackage: null, NugetFeed: itemData };
    }
}
